// src/hr/portal/portalUtils.js
// Words, dates and small calculations shared by the HR Portal screens. Time is stored in hours
// (1 day = 8); dates are 'YYYY-MM-DD' strings handled without any timezone shift.
import { daysLabel, daysNumber, shortRangeLabel } from '../format';
import { addDays, daysBetween, isDateString, shortDate } from '../timeoff/dates';
import { typeLabel } from '../timeoff/labels';

export const HOURS_PER_DAY = 8;
export const SICK_MIN_HOURS = 40;
export const SICK_MIN_MESSAGE = 'Sick & Safe must be at least 40 hours';
// "Low PTO" on the People page: less than 2 days left to book.
export const LOW_PTO_HOURS = 16;
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const EMPLOYMENT_TYPES = [
  { value: 'full-time', label: 'Full-time' },
  { value: 'part-time', label: 'Part-time' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'intern', label: 'Intern' }
];

export const fullName = (p) => (p ? [p.firstName, p.lastName].filter(Boolean).join(' ') : '');

// 'Priya Shah' -> 'Priya S.' (stage chips on the Requests page).
export function shortName(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return parts[0] || '';
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

// Still on the books: not archived and not marked left.
export const isCurrent = (p) => !!p && !p.archived && !p.archivedAt && p.status !== 'left';

export function employmentLabel(value) {
  const hit = EMPLOYMENT_TYPES.find((t) => t.value === value);
  return hit ? hit.label : 'Full-time';
}

// Account pill for the People list and the profile header.
export function accountPill(status, { long = false } = {}) {
  if (status === 'linked') return { tone: 'ok', label: long ? 'Linked to SaaS login' : 'Linked' };
  if (status === 'awaiting') return { tone: 'warn', label: 'Awaiting approval' };
  return { tone: 'warn', label: 'Not signed in yet' };
}

// Lifecycle pill (none while simply active and on the books).
export function lifecyclePill(p) {
  if (!p) return null;
  if (p.archived || p.archivedAt) return { tone: 'mute', label: 'Archived' };
  if (p.status === 'left') return { tone: 'mute', label: p.lastDay ? `Left ${shortDate(p.lastDay)}` : 'Left' };
  if (p.status === 'leaving') return { tone: 'bad', label: p.lastDay ? `Leaving ${shortDate(p.lastDay)}` : 'Leaving' };
  return null;
}

// The benefit year containing `today` for a benefit year that starts on `startMD` ('MM-DD').
export function benefitYearRange(today, startMD = '01-01') {
  const md = /^\d{2}-\d{2}$/.test(String(startMD || '')) ? startMD : '01-01';
  const y = Number(String(today).slice(0, 4));
  const year = String(today).slice(5) < md ? y - 1 : y;
  return { year, start: `${year}-${md}`, end: addDays(`${year + 1}-${md}`, -1) };
}

// 'Mar 14, 2023'
export function fullDate(date) {
  return isDateString(date) ? `${shortDate(date)}, ${String(date).slice(0, 4)}` : '';
}

// A timestamp's date in New York as 'Oct 30 · 4:12 PM'.
export function stampLabel(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const day = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' }).format(d);
  const time = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' }).format(d);
  return `${day} · ${time}`;
}

// '3 yrs 7 mo' since the hire date; 'Starts Dec 1' for a future hire date.
export function tenureLabel(hireDate, today) {
  if (!isDateString(hireDate)) return '';
  if (hireDate > today) return `Starts ${shortDate(hireDate)}`;
  const [hy, hm, hd] = hireDate.split('-').map(Number);
  const [ty, tm, td] = today.split('-').map(Number);
  const months = (ty - hy) * 12 + (tm - hm) - (td < hd ? 1 : 0);
  const years = Math.floor(months / 12);
  const rest = months % 12;
  if (months <= 0) return 'Joined this month';
  const parts = [];
  if (years > 0) parts.push(`${years} ${years === 1 ? 'yr' : 'yrs'}`);
  if (rest > 0) parts.push(`${rest} mo`);
  return parts.join(' ');
}

// The next work anniversary on or after today (never the hire date itself): { date, years, inDays }.
// A Feb 29 hire date has its anniversary on Feb 28 in other years.
export function nextAnniversary(hireDate, today) {
  if (!isDateString(hireDate) || hireDate > today) return null;
  const md = hireDate.slice(5);
  const hireYear = Number(hireDate.slice(0, 4));
  const at = (year) => (isDateString(`${year}-${md}`) ? `${year}-${md}` : `${year}-02-28`);
  let year = Math.max(Number(today.slice(0, 4)), hireYear + 1);
  if (at(year) < today) year += 1;
  const date = at(year);
  return { date, years: year - hireYear, inDays: daysBetween(today, date) };
}

export function anniversaryLabel(hireDate, today) {
  const next = nextAnniversary(hireDate, today);
  if (!next) return '';
  const span = `${next.years} ${next.years === 1 ? 'year' : 'years'}`;
  if (next.inDays === 0) return `${span} today`;
  return `${span} in ${next.inDays} ${next.inDays === 1 ? 'day' : 'days'}`;
}

// Hours in the allowance editor's unit: days (default) or hours. Never rounded -- 8 is a power of
// two, so hours / 8 and days * 8 are exact and a value can go back and forth without drifting.
// (Rounded day values are for display only: unitNumber / daysNumber.)
export const toUnit = (hours, unit) => {
  const n = Number(hours) || 0;
  return unit === 'hours' ? n : n / HOURS_PER_DAY;
};
export const fromUnit = (value, unit) => (unit === 'hours' ? Number(value) : Number(value) * HOURS_PER_DAY);
export const unitNumber = (hours, unit) => (unit === 'hours' ? String(Math.round((Number(hours) || 0) * 100) / 100) : daysNumber(hours));

// An editable days cell that keeps its exact hours (the year-end table): the text box only shows
// the hours in days, and only typing changes them -- a value is never re-read from rounded text.
export const daysCell = (hours) => {
  const exact = Number(hours) || 0;
  return { text: String(toUnit(exact, 'days')), hours: exact };
};
export function typedDaysCell(text) {
  const n = Number(text);
  return { text, hours: String(text).trim() !== '' && Number.isFinite(n) ? fromUnit(n, 'days') : NaN };
}

// A work authorization runs out on its expiry date, or 240 days later when an extension was filed
// (the server's reminderMath.effectiveDueDate). '' without a usable expiry date.
export const WORK_AUTH_EXTENSION_DAYS = 240;
export function workAuthEffectiveDate(expiresOn, extensionFiledOn) {
  if (!isDateString(expiresOn)) return '';
  return extensionFiledOn ? addDays(expiresOn, WORK_AUTH_EXTENSION_DAYS) : expiresOn;
}

// The person on HR's list that a sign-in address belongs to (any of their addresses), or null.
export function personForEmail(people, email) {
  const address = String(email || '').trim().toLowerCase();
  if (!address) return null;
  return (people || []).find((p) => [p.email, ...(p.emails || [])].some((e) => String(e || '').toLowerCase() === address)) || null;
}

// Running totals for protected leave (spec M6): SAFE Act 20 days in 12 months, NJFLA 12 weeks in 24.
export function protectedTotalText(total) {
  if (!total) return '';
  if (total.key === 'safe') return `${total.workdaysLast12Months} of 20 days in the last 12 months`;
  if (total.key === 'njfla') {
    const weeks = Math.round((total.workdaysLast24Months / 5) * 10) / 10;
    return `${weeks} of 12 weeks in the last 24 months`;
  }
  const n = total.workdaysLast12Months;
  return `${n} ${n === 1 ? 'day' : 'days'} in the last 12 months`;
}

// Share of the legal limit used, for the bar under a running total (null when there is no limit).
export function protectedShare(total) {
  if (!total) return null;
  if (total.key === 'safe') return Math.min(1, total.workdaysLast12Months / 20);
  if (total.key === 'njfla') return Math.min(1, total.workdaysLast24Months / 60);
  return null;
}

// Whether a sick entry overlaps one of the person's 3+ consecutive sick-day runs (spec S7).
export function inSickRun(entry, runs) {
  if (!entry || !entry.isSick) return false;
  return (runs || []).some((run) => entry.startDate <= run.endDate && entry.endDate >= run.startDate);
}

// ---------------------------------------------------------------------------------------------
// Change log wording. Rows carry ids, dates, hours and field names only -- never notes or reasons.
// ---------------------------------------------------------------------------------------------

const FIELD_WORDS = {
  firstName: 'first name',
  lastName: 'last name',
  email: 'email',
  emails: 'email addresses',
  title: 'title',
  department: 'department',
  managerId: 'manager',
  approverId: 'approver',
  notifyIds: 'also-notify list',
  hireDate: 'hire date',
  employmentType: 'employment type',
  status: 'status',
  lastDay: 'last day',
  archivedAt: 'archive date',
  retainUntil: 'retention date',
  legalHold: 'legal hold',
  rehire: 'rehire record',
  startDate: 'dates',
  endDate: 'dates',
  hoursPerDay: 'length',
  totalHours: 'hours',
  note: 'note',
  docRequested: 'documentation requested',
  docReceived: 'documentation received'
};
const ALLOWANCE_WORDS = { allowanceHours: 'this year', carriedOverHours: 'carried over', usedBeforePortalHours: 'used before the portal' };
const STATUS_WORDS = { active: 'Active', leaving: 'Leaving', left: 'Left' };

function fieldList(fields) {
  const words = [...new Set((fields || []).map((f) => FIELD_WORDS[f] || f))];
  if (words.length <= 1) return words[0] || 'details';
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

export function actorLabel(row) {
  if (!row) return '';
  if (row.actorEmail === 'system') return 'System';
  return row.actorName || row.actorEmail || 'Someone';
}

// One readable line for a change-log row, e.g. "logged PTO Nov 27 (1 day)".
export function describeChange(row, typesById) {
  const after = (row && row.after) || {};
  const before = (row && row.before) || {};
  const typeName = (id) => {
    const t = typesById && id ? typesById.get(String(id)) : null;
    return t ? typeLabel(t) : 'time off';
  };
  const range = (snap) => shortRangeLabel(snap.startDate, snap.endDate);
  switch (row && row.action) {
    case 'entry.create':
      return `logged ${typeName(after.typeId)} ${range(after)} (${daysLabel(after.totalHours)})`;
    case 'entry.delete':
      return `removed ${typeName(before.typeId)} ${range(before)} (${daysLabel(before.totalHours)})`;
    case 'entry.update':
      return `changed the ${fieldList(after.fields)} of an entry`;
    case 'allowance.update': {
      const name = typeName(after.typeId);
      const changes = Object.keys(ALLOWANCE_WORDS)
        .filter((f) => (Number(before[f]) || 0) !== (Number(after[f]) || 0))
        .map((f) => `${ALLOWANCE_WORDS[f]} ${daysNumber(before[f])} → ${daysNumber(after[f])}`);
      return changes.length ? `set ${name} ${changes.join(', ')} (days)` : `saved ${name} allowances`;
    }
    case 'person.create': return 'added this person';
    case 'person.update': {
      if ((after.fields || []).length === 1 && after.fields[0] === 'status' && STATUS_WORDS[after.status]) {
        return `set the status to ${STATUS_WORDS[after.status]}`;
      }
      return `updated the ${fieldList(after.fields)}`;
    }
    case 'person.archive': return 'archived this person';
    case 'person.legalHold': return after.legalHold ? 'turned legal hold on' : 'turned legal hold off';
    case 'person.restoreSick': return 'restored unused Sick & Safe hours';
    case 'linkAccount': return 'linked their SaaS login';
    case 'sickGrant': return `granted the ${after.year || ''} Sick & Safe hours`.replace('  ', ' ');
    case 'sick.report': return 'reported a sick day';
    case 'request.submit': return 'sent a time-off request';
    case 'request.approve': return 'approved a request';
    case 'request.approve_for_manager': return 'approved a request for the manager';
    case 'request.decline': return 'declined a request';
    case 'request.cancel': return 'cancelled a request';
    case 'request.reassign': return after.status === 'pending_hr'
      ? 'sent a waiting request to HR (approver removed)'
      : 'moved a waiting request to the new approver';
    case 'checklist.start': return `started the ${after.kind || ''} checklist`.replace('  ', ' ');
    case 'checklist.item': return after.done ? 'ticked a checklist item' : 'unticked a checklist item';
    case 'policy.ack': return 'acknowledged a policy';
    case 'reminder.create': return 'added a reminder';
    case 'reminder.update': return 'updated a reminder';
    case 'reminder.delete': return 'removed a reminder';
    default: return String((row && row.action) || 'made a change').replace(/[._]/g, ' ');
  }
}
